import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchGalleryImages, galleryParamsSchema, type GalleryFilter } from '../gallery/data'
import { parseWidgetConfig } from '@homerunner-next/widget-core/schema'
import type { CaptionPhoto } from '../../common/bedroom-captions'

/**
 * The property's photos for caption fill (dashboard#1707), read through the
 * key the Gallery widget owns: `['gallery-images', filter]`.
 *
 * This observer is PASSIVE (`enabled: false`): it never starts a request. When
 * a Gallery widget on the same page has loaded (or SSR-seeded) that key, the
 * shared client hands this observer the same data; with no Gallery widget the
 * key is empty and caption fill simply has nothing to offer, so a page never
 * pays a request for it. The fetcher is the gallery's own `fetchGalleryImages`
 * and the key is built the way the gallery builds it (`galleryParamsSchema`
 * defaults included), so if the key is ever fetched from here it is by the
 * same function, not a second one.
 *
 * Best-effort match: the key only lines up with a Gallery widget configured
 * for the same property with the default page size. A Gallery with a custom
 * limit lives under a different key and caption fill stays empty.
 *
 * The filter goes through `parseWidgetConfig` (null leaves stripped, scalars
 * coerced, then `galleryParamsSchema.parse`), the same path the Gallery widget
 * takes for its own `filter`, so the two keys hash identically and a filter
 * the Gallery itself could not parse throws for the render boundary instead
 * of silently producing an empty caption set (#1168).
 */
export function galleryFilterFor(filter: { feed_id?: number; property?: string; platform?: string | null } | undefined): GalleryFilter | null {
  if (!filter?.property || !filter.feed_id) return null
  const candidate: Record<string, unknown> = { feed_id: filter.feed_id, property: filter.property }
  if (filter.platform !== undefined) candidate.platform = filter.platform
  return parseWidgetConfig(galleryParamsSchema, candidate, (v) => v)
}

export function useSharedGalleryPhotos(filter: GalleryFilter | null, enabled: boolean): CaptionPhoto[] {
  const key = filter ?? { property: '' }
  const { data } = useInfiniteQuery({
    queryKey: ['gallery-images', key],
    queryFn: ({ pageParam = 1 }) => fetchGalleryImages({ ...(key as GalleryFilter), page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage?.pagination?.hasNextPage ? lastPage.pagination.page + 1 : undefined),
    // Never fetches: enabled is false even when caption fill is on.
    enabled: false,
  })
  if (!enabled || !filter) return []
  const photos: CaptionPhoto[] = []
  for (const page of data?.pages ?? []) {
    for (const image of page?.result ?? []) {
      if (image?.url) photos.push({ url: image.url, caption: image.caption ?? '' })
    }
  }
  return photos
}
